import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BellRing, CalendarDays, Copy, FileSpreadsheet, History, Save, Target, UserCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "./common";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg, fmtINR, downloadBlob } from "../lib/api";
import { StaffLabel } from "./StaffAvatar";
import { TARGET_STATUS_META } from "./SupervisorTargetDialog";
import { TargetHistoryCard } from "./TargetHistoryCard";
import { ReadOnlyNotice } from "./FeatureGate";
import { DateRangePicker } from "./DateRangePicker";
import { MonthlyTargetsDialog } from "./MonthlyTargetsDialog";
import { presetRange, rangeParams, singleMonthOf, isFullMonths, prevMonth, monthLabel, rangeLabel } from "../lib/dateRange";

const StatusBadge = ({ status }) => {
  const m = TARGET_STATUS_META[status] || TARGET_STATUS_META.no_target;
  return <Badge variant="outline" className={`text-[10px] ${m.cls}`}>{m.label}</Badge>;
};

/**
 * Users & Staff → "Supervisor Targets".
 * Phase 175: a From/To range (default: this month) drives every figure — monthly targets are pro-rated by the days of
 * each month inside the range, collections / expected / achievement are computed inside the range. Editing stays
 * monthly: a range inside ONE month edits that month inline; a multi-month range opens a month-by-month editor.
 */
export function SupervisorTargetsTab({ code, readOnly = false }) {
  const [range, setRange] = useState(() => presetRange("this_month"));
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});   // {supervisor_id: {personal_target: str, team_goal: str}} — single-month mode only
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);   // supervisor row → history dialog
  const [monthsFor, setMonthsFor] = useState(null);     // supervisor row → month-by-month editor (multi-month range)
  const [alerts, setAlerts] = useState(null);           // behind-pace alert log for the range's months
  const [checking, setChecking] = useState(false);

  const month = singleMonthOf(range);                   // 'YYYY-MM' when the range sits inside one month, else null
  const partial = !isFullMonths(range);
  const label = rangeLabel(range);

  const loadAlerts = useCallback(() =>
    api.get(`/t/${code}/supervisor-targets/alerts`, { params: rangeParams(range) }).then((r) => setAlerts(r.data)).catch(() => setAlerts(null)),
  [code, range]);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const checkPaceNow = async () => {
    setChecking(true);
    try {
      const r = await api.post(`/t/${code}/supervisor-targets/alerts/check`, null, { params: { force: true } });
      const d = r.data;
      if (d.alerts_sent > 0) {
        toast.success(`${d.alerts_sent} behind-pace alert${d.alerts_sent === 1 ? "" : "s"} sent to supervisors and admins (day ${d.day})`);
      } else if (d.behind_targets > 0) {
        toast.info(`All ${d.behind_targets} behind-pace target${d.behind_targets === 1 ? " was" : "s were"} already alerted today — nothing new sent`);
      } else {
        toast.success(`Checked ${d.supervisors_checked} supervisor${d.supervisors_checked === 1 ? "" : "s"} — no target is behind pace`);
      }
      loadAlerts();
    } catch (e) { toast.error(errMsg(e)); } finally { setChecking(false); }
  };

  const load = useCallback(() => {
    setData(null);
    return api.get(`/t/${code}/supervisor-targets`, { params: rangeParams(range) }).then((r) => {
      setData(r.data);
      const e = {};
      (r.data.supervisors || []).forEach((s) => {
        const m0 = (s.months || [])[0] || {};
        e[s.supervisor_id] = {
          personal_target: m0.personal_target != null ? String(m0.personal_target) : "",
          team_goal: m0.team_goal != null ? String(m0.team_goal) : "",
        };
      });
      setEdits(e);
    });
  }, [code, range]);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  // the monthly value behind the inline inputs (single-month mode) — the range figure is pro-rated from it
  const monthly = (s) => (s.months || [])[0] || {};
  const dirty = useMemo(() => {
    if (!data || !month) return [];
    return (data.supervisors || []).filter((s) => {
      const e = edits[s.supervisor_id] || {};
      const m0 = monthly(s);
      const p = m0.personal_target != null ? String(m0.personal_target) : "";
      const g = m0.team_goal != null ? String(m0.team_goal) : "";
      return (e.personal_target ?? "") !== p || (e.team_goal ?? "") !== g;
    });
  }, [data, edits, month]);

  const setField = (id, field, value) => setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [field]: value } }));

  const saveAll = async () => {
    if (dirty.length === 0 || !month) return;
    setBusy(true);
    try {
      const items = dirty.map((s) => ({
        supervisor_id: s.supervisor_id,
        personal_target: edits[s.supervisor_id].personal_target === "" ? null : Number(edits[s.supervisor_id].personal_target),
        team_goal: edits[s.supervisor_id].team_goal === "" ? null : Number(edits[s.supervisor_id].team_goal),
      }));
      const r = await api.put(`/t/${code}/supervisor-targets/bulk`, { month, items });
      toast.success(`Targets saved for ${r.data.saved} supervisor${r.data.saved === 1 ? "" : "s"} (${monthLabel(month, true)})`);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const copyPrevious = async () => {
    if (!month) return;
    const pm = prevMonth(month);
    try {
      const r = await api.get(`/t/${code}/supervisor-targets`, { params: { month: pm } });
      const rows = (r.data.supervisors || []).filter((s) => s.personal_target != null || s.team_goal != null);
      if (rows.length === 0) { toast.info(`No supervisor targets were set in ${monthLabel(pm, true)}`); return; }
      setEdits((e) => {
        const n = { ...e };
        rows.forEach((s) => {
          n[s.supervisor_id] = {
            personal_target: s.personal_target != null ? String(s.personal_target) : "",
            team_goal: s.team_goal != null ? String(s.team_goal) : "",
          };
        });
        return n;
      });
      toast.success(`Copied ${rows.length} supervisor target${rows.length === 1 ? "" : "s"} from ${monthLabel(pm, true)} — review and click Save all`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      await downloadBlob(`/t/${code}/supervisor-targets/export?from=${range.from}&to=${range.to}`, `supervisor-targets_${range.from}_to_${range.to}.xlsx`);
      toast.success(`Supervisor targets exported (${label})`);
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const t = data?.totals || {};
  const sups = data?.supervisors || [];
  const pace = data ? { elapsed: data.elapsed_days, days: data.days_in_month } : null;
  const monthsTouched = data?.range?.months?.length || 1;

  return (
    <div className="space-y-4" data-testid="supervisor-targets-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <DateRangePicker value={range} onChange={setRange} pace={pace} testIdPrefix="supervisor-targets-range" />
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={exporting || !data} data-testid="supervisor-targets-export">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> {exporting ? "Exporting..." : "Export Excel"}
          </Button>
          {!readOnly && <>
            <Button variant="outline" size="sm" onClick={checkPaceNow} disabled={checking} data-testid="supervisor-targets-check-pace">
              <BellRing className="h-4 w-4 mr-1.5" /> {checking ? "Checking..." : "Check pace now"}
            </Button>
            {month && (
              <Button variant="outline" size="sm" onClick={copyPrevious} disabled={busy} data-testid="supervisor-targets-copy-previous">
                <Copy className="h-4 w-4 mr-1.5" /> Copy from {monthLabel(prevMonth(month))}
              </Button>
            )}
            {month && (
              <Button size="sm" onClick={saveAll} disabled={busy || dirty.length === 0} data-testid="supervisor-targets-save-all">
                <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving..." : dirty.length ? `Save all (${dirty.length})` : "Save all"}
              </Button>
            )}
          </>}
        </div>
      </div>
      {readOnly && <ReadOnlyNotice what="supervisor targets" testId="supervisor-targets-readonly-notice" />}

      {!month && (
        <p className="text-xs rounded-md border bg-muted/40 px-3 py-2 flex items-start gap-2" data-testid="supervisor-targets-multi-month-note">
          <CalendarDays className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          <span>
            <b>{label}</b> spans {monthsTouched} months. Targets below are the monthly targets <b>pro-rated by days</b> inside the range
            (e.g. 15 Sep – 15 Oct = Sep × 16/30 + Oct × 15/31); collections, expected and achievement are for the range only.
            {!readOnly && <> Targets are still set per month — use <b>Set monthly targets</b> on a row.</>}
          </span>
        </p>
      )}
      {month && partial && (
        <p className="text-xs rounded-md border bg-muted/40 px-3 py-2 flex items-start gap-2" data-testid="supervisor-targets-partial-note">
          <CalendarDays className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          <span>
            <b>{label}</b> covers {data?.days_in_month ?? ""} of {monthly(sups[0] || {}).days_in_month || ""} days of {monthLabel(month, true)}. The inputs edit the
            <b> full monthly target</b>; the figures beside them show how much of it falls inside the range and the collections made in it.
          </span>
        </p>
      )}

      <p className="text-xs text-muted-foreground -mt-1" data-testid="supervisor-targets-alerts-info">
        <BellRing className="h-3 w-3 inline mr-1 -mt-0.5" />
        Behind-pace alerts go to the supervisor and all admins automatically on the <b>10th, 15th, 20th and 25th</b>
        {alerts ? <> · <b data-testid="supervisor-targets-alerts-count">{alerts.alerts.length}</b> alert{alerts.alerts.length === 1 ? "" : "s"} sent in {label}</> : null}
        . <i>Check pace now</i> runs the same check immediately (no duplicates on the same day).
      </p>

      {!data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Card className="shadow-sm"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><UserCheck className="h-3.5 w-3.5" /> Personal targets{!month || partial ? " (range)" : ""}</p>
              <p className="text-lg font-semibold tabular-nums" data-testid="supervisor-targets-total-personal">{fmtINR(t.personal_target)}</p>
              <p className="text-[11px] text-muted-foreground tabular-nums">
                collected {fmtINR(t.personal_collected)}{t.personal_achievement_pct != null && <> · <span data-testid="supervisor-targets-total-personal-pct">{t.personal_achievement_pct}%</span></>}
              </p>
            </CardContent></Card>
            <Card className="shadow-sm"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Team goals (Admin){!month || partial ? " (range)" : ""}</p>
              <p className="text-lg font-semibold tabular-nums" data-testid="supervisor-targets-total-goal">{fmtINR(t.team_goal)}</p>
              <p className="text-[11px] text-muted-foreground tabular-nums">
                teams collected {fmtINR(t.team_collected)}{t.team_goal_achievement_pct != null && <> · <span data-testid="supervisor-targets-total-goal-pct">{t.team_goal_achievement_pct}%</span></>}
              </p>
            </CardContent></Card>
            <Card className="shadow-sm"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Team targets (sum of collectors)</p>
              <p className="text-lg font-semibold tabular-nums" data-testid="supervisor-targets-total-team-target">{fmtINR(t.team_target)}</p>
              <p className="text-[11px] text-muted-foreground">bottom-up collector targets{!month || partial ? ", pro-rated for the range" : ""}</p>
            </CardContent></Card>
          </div>

          {sups.length === 0 ? (
            <EmptyState title="No supervisors yet" subtitle="Add supervisors in the Staff directory first." />
          ) : (
            <Card className="shadow-sm">
              <CardContent className="p-0 overflow-x-auto">
                <Table data-testid="supervisor-targets-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supervisor</TableHead>
                      <TableHead className="min-w-[190px]">{month ? `Personal target (₹) · ${monthLabel(month)}` : "Personal target (₹, range)"}</TableHead>
                      <TableHead className="min-w-[200px]">Own collections</TableHead>
                      <TableHead className="min-w-[190px]">{month ? `Team goal (₹) · ${monthLabel(month)}` : "Team goal (₹, range)"}</TableHead>
                      <TableHead className="min-w-[200px]">Team collections</TableHead>
                      <TableHead className="text-right">Team target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sups.map((s) => {
                      const e = edits[s.supervisor_id] || { personal_target: "", team_goal: "" };
                      const pPct = s.personal_target ? Math.min(100, Math.round(s.personal_collected * 100 / s.personal_target)) : 0;
                      const gPct = s.team_goal ? Math.min(100, Math.round(s.team_collected * 100 / s.team_goal)) : 0;
                      const isDirty = dirty.some((d) => d.supervisor_id === s.supervisor_id);
                      const perMonth = (key) => (s.months || []).filter((m) => m[key] != null).map((m) => `${m.label}: ${fmtINR(m[key])}`).join(" · ");
                      return (
                        <TableRow key={s.supervisor_id} className={`${!s.is_active ? "opacity-60" : ""} ${isDirty ? "bg-primary/[0.04]" : ""}`}
                          data-testid={`supervisor-target-row-${s.email}`}>
                          <TableCell>
                            <div className="font-medium text-sm"><StaffLabel code={code} userId={s.supervisor_id} name={s.supervisor_name} />{!s.is_active && <Badge variant="outline" className="ml-2 text-[10px]">Inactive</Badge>}</div>
                            <p className="text-[11px] text-muted-foreground">
                              {s.collectors} collector{s.collectors === 1 ? "" : "s"}{s.zone_names.length ? ` • ${s.zone_names.join(", ")}` : ""}
                              {s.set_by_name && <> • set by <StaffLabel code={code} userId={s.set_by} name={s.set_by_name} /></>}
                            </p>
                            <div className="flex items-center gap-3 mt-0.5">
                              <Button variant="link" size="sm" className="h-auto p-0 text-[11px]" onClick={() => setHistoryFor(s)}
                                data-testid={`supervisor-target-history-${s.email}`}>
                                <History className="h-3 w-3 mr-1" /> History
                              </Button>
                              {!month && !readOnly && (
                                <Button variant="link" size="sm" className="h-auto p-0 text-[11px]" onClick={() => setMonthsFor(s)}
                                  data-testid={`supervisor-target-edit-months-${s.email}`}>
                                  <CalendarDays className="h-3 w-3 mr-1" /> Set monthly targets
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {month ? (
                              <div className="space-y-1">
                                <Input type="number" min="0" step="100" value={e.personal_target} placeholder="—" disabled={readOnly}
                                  onChange={(ev) => setField(s.supervisor_id, "personal_target", ev.target.value)}
                                  className="h-9" data-testid={`supervisor-target-personal-${s.email}`} />
                                {partial && s.personal_target != null && (
                                  <p className="text-[11px] text-muted-foreground tabular-nums" data-testid={`supervisor-target-personal-range-${s.email}`}>
                                    {fmtINR(s.personal_target)} in range
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-0.5" data-testid={`supervisor-target-personal-range-${s.email}`}>
                                <p className="tabular-nums text-sm font-medium">{s.personal_target != null ? fmtINR(s.personal_target) : <span className="text-muted-foreground">—</span>}</p>
                                <p className="text-[11px] text-muted-foreground truncate max-w-[220px]" title={perMonth("personal_target")}>{perMonth("personal_target") || "no monthly targets"}</p>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between gap-2 text-sm">
                                <span className="tabular-nums font-medium">{fmtINR(s.personal_collected)}</span>
                                <StatusBadge status={s.personal_status} />
                              </div>
                              {s.personal_target > 0 && (
                                <div className="flex items-center gap-2">
                                  <Progress value={pPct} className="h-1.5" />
                                  <span className="text-[11px] tabular-nums text-muted-foreground w-10 text-right">{s.personal_achievement_pct}%</span>
                                </div>
                              )}
                              {s.personal_expected_to_date != null && (
                                <p className="text-[11px] text-muted-foreground tabular-nums">expected by today {fmtINR(s.personal_expected_to_date)}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {month ? (
                              <div className="space-y-1">
                                <Input type="number" min="0" step="1000" value={e.team_goal} placeholder="—" disabled={readOnly}
                                  onChange={(ev) => setField(s.supervisor_id, "team_goal", ev.target.value)}
                                  className="h-9" data-testid={`supervisor-target-goal-${s.email}`} />
                                {partial && s.team_goal != null && (
                                  <p className="text-[11px] text-muted-foreground tabular-nums" data-testid={`supervisor-target-goal-range-${s.email}`}>
                                    {fmtINR(s.team_goal)} in range
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-0.5" data-testid={`supervisor-target-goal-range-${s.email}`}>
                                <p className="tabular-nums text-sm font-medium">{s.team_goal != null ? fmtINR(s.team_goal) : <span className="text-muted-foreground">—</span>}</p>
                                <p className="text-[11px] text-muted-foreground truncate max-w-[220px]" title={perMonth("team_goal")}>{perMonth("team_goal") || "no monthly goals"}</p>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between gap-2 text-sm">
                                <span className="tabular-nums font-medium">{fmtINR(s.team_collected)}</span>
                                <StatusBadge status={s.team_goal_status} />
                              </div>
                              {s.team_goal > 0 && (
                                <div className="flex items-center gap-2">
                                  <Progress value={gPct} className="h-1.5" />
                                  <span className="text-[11px] tabular-nums text-muted-foreground w-10 text-right">{s.team_goal_achievement_pct}%</span>
                                </div>
                              )}
                              {s.team_goal_expected_to_date != null && (
                                <p className="text-[11px] text-muted-foreground tabular-nums">expected by today {fmtINR(s.team_goal_expected_to_date)}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <p className="tabular-nums text-sm font-medium">{fmtINR(s.team_target)}</p>
                            <p className="text-[11px] text-muted-foreground">{s.team_target_achievement_pct != null ? `${s.team_target_achievement_pct}% achieved` : "no collector targets"}</p>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
      <p className="text-xs text-muted-foreground">
        <b>Personal target</b> is measured against payments the supervisor collects personally. <b>Team goal</b> is the Admin's top-down goal for the whole
        team, shown beside the bottom-up <b>Team target</b> (sum of the collector targets). <b>Expected by today</b> = range target × days elapsed ÷ days in range.
        Blank or 0 clears a target.
      </p>

      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent className="max-w-3xl" data-testid="supervisor-target-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-base">
              <History className="h-4 w-4" /> Target history — <StaffLabel code={code} userId={historyFor?.supervisor_id} name={historyFor?.supervisor_name} className="ml-1" />
            </DialogTitle>
          </DialogHeader>
          {historyFor && <TargetHistoryCard code={code} range={range} endMonth={range.to.slice(0, 7)} supervisorId={historyFor.supervisor_id} title="Month-by-month achievement" />}
        </DialogContent>
      </Dialog>

      {monthsFor && (
        <MonthlyTargetsDialog code={code} kind="supervisor" row={monthsFor} rangeLabel={label} open={!!monthsFor}
          onOpenChange={(v) => !v && setMonthsFor(null)} onSaved={() => load().catch((e) => toast.error(errMsg(e)))} />
      )}
    </div>
  );
}
